import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
import "core-js/modules/web.url-search-params.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.replace.js";
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.array.from.js";
import "core-js/modules/es.array.filter.js";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import brightcovePlayerLoader from '@brightcove/player-loader';
import useCallbackRef from 'hooks/useCallbackRef';
import useLocalStorage from 'hooks/useLocalStorage';
import config from 'utils/config';
import { useFetchUserSettings, useUpdateMyUserSettings } from 'actions/userSettings';
import { STATUS_COMPLETED } from 'utils/asset/getUserAssetStatus';
// Can't bring the types from VideoJS so this is a workaround 😕

var DEFAULT_BRIGHTCOVE_PLAYER_ID = 'default'; // the ID of the default account player in Brightcove is always 'default'

var VideoJs = styled.video.attrs({
  className: 'video-js'
}).withConfig({
  displayName: "BrightcovePlayer__VideoJs",
  componentId: "sc-16ggh7-0"
})(["width:100%;height:100%;.vjs-big-play-button{background-color:", ";opacity:0.8;transition:opacity 0.25s linear;}&&& .vjs-menu-item.vjs-selected,&:hover .vjs-big-play-button{background-color:", ";opacity:1;}.vjs-volume-level,.vjs-play-progress{background:", ";}.vjs-picture-in-picture-control{display:none;}"], function (p) {
  return p.theme.globals.buttons.default.text;
}, function (p) {
  return p.theme.globals.buttons.default.text;
}, function (p) {
  return p.theme.globals.buttons.default.text;
});
var FullScreen = styled.div.withConfig({
  displayName: "BrightcovePlayer__FullScreen",
  componentId: "sc-16ggh7-1"
})(["width:100%;height:100%;"]);
function isVideoAlmostFinished(currentTime, duration) {
  if (!duration) {
    return false;
  }
  return currentTime * 100 / duration >= 95; // we watched 95% or more
}
function BrightcovePlayer(_ref) {
  var accountId = _ref.accountId,
    videoId = _ref.videoId,
    autoCompleteEnabled = _ref.autoCompleteEnabled,
    userContentStatus = _ref.userContentStatus,
    onLoad = _ref.onLoad,
    onError = _ref.onError,
    onComplete = _ref.onComplete;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var playerId = config.brightcovePlayerId || DEFAULT_BRIGHTCOVE_PLAYER_ID;
  var playerElementRef = useRef(null);
  var _useLocalStorage = useLocalStorage("video-".concat(videoId, "-progress")),
    _useLocalStorage2 = _slicedToArray(_useLocalStorage, 3),
    progress = _useLocalStorage2[0],
    setProgress = _useLocalStorage2[1],
    deleteProgress = _useLocalStorage2[2];
  var _useFetchUserSettings = useFetchUserSettings(),
    userSettings = _useFetchUserSettings.userSettings,
    isLoading = _useFetchUserSettings.isLoading;
  var subtitlesLanguage = (userSettings === null || userSettings === void 0 ? void 0 : userSettings.subtitlesLanguage) || null;
  var _useUpdateMyUserSetti = useUpdateMyUserSettings(),
    updateUserSettings = _useUpdateMyUserSetti.updateUserSettings;
  var _useState = useState(userContentStatus),
    _useState2 = _slicedToArray(_useState, 2),
    status = _useState2[0],
    setStatus = _useState2[1];
  var wasAssetCompletedRef = useRef(userContentStatus === STATUS_COMPLETED);

  // Set the current Time of the video from the hash parameter
  var hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  var initialTime = hashParams.get('currentTime') || hashParams.get('position'); // Seconds
  var autoplay = hashParams.get('autoplay');
  useEffect(function () {
    if (initialTime) {
      setProgress(Number(initialTime));
    }
  }, [initialTime, setProgress]);
  useEffect(function () {
    if (autoCompleteEnabled && status === STATUS_COMPLETED && !wasAssetCompletedRef.current && typeof onComplete === 'function') {
      wasAssetCompletedRef.current = true;
      onComplete();
    }
  }, [accountId, videoId, autoCompleteEnabled, onComplete, status]);

  // We're creating refs for the updateProgress and manageSubtitles callbacks
  // so that we can reference their latest versions from within the effect that
  // sets up the Brightcove player (brightcovePlayerLoader)
  var updateProgressRef = useCallbackRef(function (player) {
    return function () {
      var currentTime = player.currentTime();
      setProgress(currentTime);
      if (status !== STATUS_COMPLETED && !player.paused() && isVideoAlmostFinished(currentTime, player.duration())) {
        setStatus(STATUS_COMPLETED);
      }
    };
  });
  var manageSubtitlesRef = useCallbackRef(function (player) {
    var allTextTracks = Array.from(player.textTracks()); // subtitles + metadata tracks
    var subtitleTracks = allTextTracks.filter(function (track) {
      return track.kind === 'subtitles' && track.language;
    });
    for (var _i = 0, _allTextTracks = allTextTracks; _i < _allTextTracks.length; _i++) {
      var track = _allTextTracks[_i];
      if (subtitlesLanguage && track.language && track.language === subtitlesLanguage) {
        track.mode = 'showing';
      }
      track.addEventListener('cuechange', function () {
        var activeTrack = subtitleTracks.filter(function (track) {
          return track.mode !== 'disabled';
        })[0];
        var activeLanguage = (activeTrack === null || activeTrack === void 0 ? void 0 : activeTrack.language) || null;
        if (activeLanguage !== subtitlesLanguage) {
          updateUserSettings({
            subtitlesLanguage: activeLanguage
          });
        }
      });
    }
  });
  useEffect(function () {
    // Watch out: Due to the incomplete dependency array of this effect,
    // any references to state outside of the effect will be stale after
    // the first call, i.e. they will not see later state changes.
    if (!isLoading) {
      brightcovePlayerLoader({
        accountId: accountId,
        playerId: playerId,
        refNode: playerElementRef.current,
        onFailure: onError,
        onSuccess: function onSuccess() {
          // Note: this callback is supposed to return a reference to the player, but actually it points to another instance of the player that can't be used to interact with the video and track progress
          // @ts-expect-error Investigate
          var player = window.videojs.players.videoplayer;
          if (!player) {
            onError({
              message: t('error_player_not_loaded')
            });
            return;
          }

          // TODO: pass more information to onError, like videoId, session, etc
          var _onError = function _onError() {
            return onError(player.error());
          };
          var deleteProgressAndMarkAsCompleted = function deleteProgressAndMarkAsCompleted() {
            deleteProgress();
            setStatus(STATUS_COMPLETED);
          };
          if (autoplay) {
            // Note autoplay requires the user to interact with the player first. We can set #autoplay=muted to start playing the video quietly in the background.
            player.autoplay(autoplay);
          }
          player.on('timeupdate', updateProgressRef.current(player));
          player.on('ended', deleteProgressAndMarkAsCompleted);
          player.on('loadeddata', onLoad);
          player.on('error', _onError);

          // resume playing the video
          if (progress) {
            player.ready(function () {
              var _this = this;
              this.currentTime(progress);
              this.one('loadedmetadata', function () {
                manageSubtitlesRef.current(player);
              });
              this.one('canplaythrough', function () {
                // Fix for Safari
                _this.currentTime(progress);
              });
            });
          }
          return function () {
            player.off('error', _onError);
            player.off('loadeddata', onLoad);
            player.off('ended', deleteProgressAndMarkAsCompleted);
            player.off('timeupdate', updateProgressRef.current);
          };
        }
      });
      return function () {
        return brightcovePlayerLoader.reset();
      };
    }
  }, [accountId, videoId, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    /*#__PURE__*/
    // The 2 divs (FullScreen) here are intentional, so that VideoJS doesn't
    // end up creating extra DOM which React will not be able to clean up
    React.createElement(FullScreen, null, /*#__PURE__*/React.createElement(FullScreen, {
      "data-vjs-player": true
    }, /*#__PURE__*/React.createElement(VideoJs, {
      id: "videoplayer",
      ref: playerElementRef,
      "data-video-id": videoId,
      "data-player": playerId,
      "data-embed": "default",
      "data-application-id": true,
      controls: true
    })))
  );
}
export default BrightcovePlayer;